/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import MuiSelect from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import React from "react";
import classnames from "classnames";

interface SelectProps {
  className?: string;
  label?: string;
  value?: string;
  options?: any[];
  fullWidth?: boolean;
  onChange?: (v: any) => any;
  [any: string]: any;
}

interface SelectState {}

class Select extends React.Component<SelectProps, SelectState> {
  state = {
    value: this.props.value || this.props.options[0].value,
  };

  onChange = (e) => {
    const { onChange } = this.props;
    const { value } = e.target;
    this.setState({ value });
    onChange(value);
  };

  renderMenuItem = (option) => {
    const isDisabled =
      typeof option.disabled === "boolean" ? option.disabled : false;

    const menuItem = (
      <MenuItem
        key={option.value}
        value={option.value}
        disabled={isDisabled}
        sx={{ width: "100%", wordWrap: "break-word" }}
      >
        {option.label}
      </MenuItem>
    );

    // If the option is disabled and has a tooltip, wrap it with Tooltip
    if (isDisabled && option.tooltip) {
      return (
        <Tooltip key={option.value} title={option.tooltip} placement="right">
          <span>{menuItem}</span>
        </Tooltip>
      );
    }

    return menuItem;
  };

  render() {
    const {
      label,
      options,
      className,
      fullWidth = true,
      onChange,
      ...rest
    } = this.props;
    const { value } = this.state;

    return (
      <FormControl fullWidth={fullWidth}>
        {label && (
          <InputLabel
            id="marapp-qa-select"
            shrink
            sx={{
              overflow: "visible",
              transform: "translate(0, -3px) scale(0.75)",
              transformOrigin: "top left",
            }}
          >
            {label}
          </InputLabel>
        )}

        <MuiSelect
          labelId="marapp-qa-select"
          variant="standard"
          value={value}
          onChange={this.onChange}
          className={classnames("marapp-qa-select", {
            [className]: !!className,
          })}
          {...rest}
        >
          {options.map((o) => this.renderMenuItem(o))}
        </MuiSelect>
      </FormControl>
    );
  }
}

export default Select;
